import { ChartDefaults } from '@/js/utils/charts/bar/dual/chartConfig';
import { renderSingleChart, renderDualChartLayout } from '@/js/utils/charts/bar/dual/barChartBuilders';
import { getBarColors, getBarColorsTop } from '@/js/utils/charts/bar/dual/chartColors';
import { getDrawBreakZigZagPlugin, getSyncChartSpacingPlugin } from '@/js/utils/charts/bar/dual/dualPlugins';
import { prepareChartMetrics } from '@/js/utils/charts/bar/dual/metrics';
import { showLoadingAndHideContent, hideLoadingAndShowContent } from '@/js/utils/ui/ui';
import { applyFiltersToUrl } from '@/js/utils/filters/active-filters';
import {startNewRequest} from "@/js/utils/net/request-guard";

const {
    SHOW_Y_GRID,
    BREAK_SVG_SRC,
    WRAPPER_CLASSES,
} = ChartDefaults;

const breakSvgImg = new Image();
breakSvgImg.src = BREAK_SVG_SRC;

/**
 * Main entry point for rendering a dual bar chart.
 * This function prepares the fetch URL with filters, triggers a loading spinner,
 * fetches the data from the server, and delegates rendering to `renderDualBarChart`.
 *
 * Typically used for rendering transmitter usage data within a selected date range.
 *
 * @param {string|null} fromDate - Start date string ('MM/DD/YYYY' or 'YYYY-MM-DD') when using custom range.
 * @param {string|null} toDate   - End date string ('MM/DD/YYYY' or 'YYYY-MM-DD') when using custom range.
 * @param {string} fetchUrl - Base API endpoint for retrieving chart data.
 * @param {string|null} period   - Preset period token (e.g., 'week', 'month', 'quarter', 'year'); null when using custom range.
 * @param {string} chartTopId - DOM ID of the top chart canvas element.
 * @param {string} chartBottomId - DOM ID of the bottom chart canvas element.
 * @param {string} chartContainerId - DOM ID of the container that holds both charts.
 * @param {string} loadingId - DOM ID of the loading spinner or overlay.
 * @param {string} chartLabel - Y-axis label for the charts (default: 'Usage (units)').
 * @param {Object} [opts]
 * @param {boolean} [opts.useServerFilters=true] Append ?key[]=... from current UI filters.
 *
 * Notes on request lifecycle:
 * - Uses startNewRequest(`chart:${chartContainerId}`) to:
 *   • abort any in-flight fetch for this chart, and
 *   • provide a `signal` for fetch cancellation and an `isStale()` guard.
 * - `fetchChartData` MUST forward { signal: req.signal } to `fetch()` and avoid invoking
 *   success callback if req.isStale() becomes true (i.e., a newer request superseded it).
 */
export function fetchAndRenderBarChart(
    fetchUrl,
    fromDate,
    toDate,
    period = null,
    chartContainerId,
    loadingId,
    chartTopId,
    chartBottomId,
    chartLabel = 'Usage (units)',
    { useServerFilters = true } = {}
) {
    // Construct the API request URL with query parameters
    const url = new URL(fetchUrl);
    if (fromDate && toDate) {
        url.searchParams.set('from_date', fromDate);
        url.searchParams.set('to_date', toDate);
    }

    if (period) {
        url.searchParams.set('period', period);
    }

    // Optional: send current UI filters to backend (keeps chart in sync with table + smaller payloads)
    if (useServerFilters) {
        applyFiltersToUrl(url); // appends ?buildings[]=&units[]=&...
    }

    // Granularity (time bucketing) — read the selected radio from the Day/Month toggle
    // and send it to the server so it can aggregate appropriately.
    // When absent, the backend should fall back to its default (e.g., 'day').
    const granularity = document.querySelector('input[name="granularity"]:checked')?.value;
    if (granularity) url.searchParams.append('granularity', granularity); // values: 'day' | 'month'

    // --- Stale-response + cancellation guard for this specific chart container ---
    //  - Aborts any previous request for the same key.
    //  - Exposes `req.signal` for fetch() and `req.isStale()` for render guards.
    const req = startNewRequest(`chart:${chartContainerId}`);

    // Display loading spinner and hide chart container
    showLoadingAndHideContent(loadingId, chartContainerId);

    // Delegate the fetch; `fetchChartData` must:
    //  - call fetch(url, { signal: req.signal })
    //  - suppress onSuccess/onError if req.isStale() is true
    //  - optionally call req.finalize() or let this caller own finalization
    fetchChartData(
        url,
        req,
        data => {
            renderDualBarChart(data, {
                chartTopId,
                chartBottomId,
                loadingId,
                chartContainerId
            });

            // Hide the spinner and show the chart again now that rendering is complete.
            hideLoadingAndShowContent(loadingId, chartContainerId);
        },
        error => {
            // AbortError = normal flow when a newer request supersedes this one.
            if (error?.name === 'AbortError') return;

            // Log and surface any other error (network/server/validation).
            console.error('Failed to fetch chart data:', error);
            // TODO: Waylon->Replace alert() with your toast/notification system
            // alert(error.details?.message || 'Unable to load chart data. Please check your date range.');

            // Restore UI state even on error (hide spinner, show container).
            hideLoadingAndShowContent(loadingId, chartContainerId);
        }
    );
}

/**
 * Fetches chart data from the backend and passes it to a rendering callback.
 *
 * @param {module:url.URL} url - The URL to fetch chart data from.
 * @param req
 * @param {Function} onSuccess - Callback to run with parsed JSON chart data.
 * @param {Function} onError - Optional error handler.
 */
function fetchChartData(url, req, onSuccess, onError = console.error) {
     fetch(url.toString(), { signal: req.signal })
         .then(async res => {
             if (!res.ok) {
                 let body = {};

                 try {
                     body = await res.json();
                 }
                 catch (_) {}

                 const err = new Error(body.message || 'Request failed.');
                 err.details = body;
                 throw err;
             }

             return res.json();
         })
         .then((data) => {
             // If a newer request started, ignore this payload
             if (req.isStale?.()) return;
             onSuccess(data);
         })
         .catch(onError);
}

/**
 * Renders a dual bar chart layout (top + bottom) based on outlier thresholds.
 * The top chart displays only extreme outliers, while the bottom chart shows the full dataset,
 * with adjusted Y-axis scaling and visual continuity using plugins.
 *
 * @param {Object} data - Parsed chart data returned from the backend.
 * @param {Object} options - Rendering configuration.
 * @param {string} options.chartTopId - DOM ID of the top chart canvas.
 * @param {string} options.chartBottomId - DOM ID of the bottom chart canvas.
 * @param {string} options.loadingId - DOM ID of the loading spinner element.
 * @param {string} options.chartContainerId - DOM ID of the wrapper that contains both charts.
 */
function renderDualBarChart(data, {
    chartTopId,
    chartBottomId,
    loadingId,
    chartContainerId
}) {
    window.addEventListener('resize', () => {
        if (window.chartRegistry[chartContainerId].top) window.chartRegistry[chartContainerId].top.resize();
        if (window.chartRegistry[chartContainerId].bottom) window.chartRegistry[chartContainerId].bottom.resize();
    });

    // Extract and transform chart data
    const { labels, dataset, extra } = data.chart;
    const { community_name: communityName, from_date, to_date } = extra;

    const usageData = dataset.data;
    const fromDateFormatted = new Date(from_date).toLocaleDateString();
    const toDateFormatted = new Date(to_date).toLocaleDateString();

    const {
        maxValue,
        moderateCutoff,
        extremeCutoff,
        outlierIndices,
        moderateIndices,
        hasOutliers,
        hasVisibleTopChart
    } = prepareChartMetrics(usageData);

    // 🎨 Determine color coding for each chart using utility functions
    const barColorsTop = getBarColorsTop(usageData, moderateCutoff);
    const barColors = getBarColors(usageData, moderateCutoff, extremeCutoff);

    // Set minimum Y-axis threshold for top chart
    const topMin = Math.ceil(Math.max(extremeCutoff, 50));

    // Destroy any existing charts
    window.chartRegistry[chartContainerId].top?.destroy();
    window.chartRegistry[chartContainerId].bottom?.destroy();

    // Prepare canvas and plugins
    const ctxTop = document.getElementById(chartTopId).getContext('2d');
    const ctxBottom = document.getElementById(chartBottomId).getContext('2d');
    const $topWrapperEl = $('#' + chartTopId).closest('.' + WRAPPER_CLASSES.topChartWrapper);
    const $bottomWrapperEl = $('#' + chartBottomId).closest('.' + WRAPPER_CLASSES.bottomChartWrapper);

    // Create plugin instance for drawing squiggly break lines above and below outlier bars, including y-axis line.
    const drawBreakZigZag = getDrawBreakZigZagPlugin(breakSvgImg, labels);

    // Create plugin instance to sync vertical spacing between top and bottom charts.
    // Ensures the bottom chart is nudged upward to align with tick labels from the top chart.
    const syncChartSpacingPlugin = getSyncChartSpacingPlugin({
        chartIdToMatch: chartTopId, // Only apply spacing when rendering this top chart
        bottomWrapperEl: $bottomWrapperEl, // jQuery reference to bottom chart wrapper element
        getAdjustedHeight: tickHeight => tickHeight + 4, // Customize spacing logic with extra padding
        shouldApplyOverlap: () => hasVisibleTopChart // Apply margin only when top chart is visible
    });

    // Group DOM references for the chart layout containers into a single object
    // These are passed to rendering and visibility utilities to control layout behavior dynamically
    const chartWrappers = {
        topWrapperEl: $topWrapperEl,     // jQuery object or DOM element for the top chart wrapper (e.g., #top-chart-wrapper)
        bottomWrapperEl: $bottomWrapperEl // jQuery object or DOM element for the bottom chart wrapper (e.g., #bottom-chart-wrapper)
    };

    // Render one or both charts based on outlier presence
    if (!hasVisibleTopChart) {
        // Bottom-only chart (no outliers)
        renderSingleChart({
            context: { ctxBottom },
            data: { labels, usageData },
            meta: {
                barColors,
                communityName,
                fromDateFormatted,
                toDateFormatted,
                hasOutliers,
                topMin,
                outlierIndices,
                yAxisWidth: null
            },
            plugins: { drawBreakZigZag, syncChartSpacingPlugin },
            options: {
                showTitle: true,
                showYGrid: SHOW_Y_GRID,
                chartWrappers,
                chartContainerId: chartContainerId
            }
        });
    } else {
        // Dual chart layout (top + bottom)
        renderDualChartLayout({
            context: { ctxTop, ctxBottom },
            data: { labels, usageData },
            meta: {
                barColors,
                barColorsTop,
                communityName,
                fromDateFormatted,
                toDateFormatted,
                hasOutliers,
                topMin,
                outlierIndices
            },
            plugins: { drawBreakZigZag, syncChartSpacingPlugin },
            options: {
                chartTopId,
                showYGrid: SHOW_Y_GRID,
                chartWrappers,
                chartContainerId: chartContainerId
            }
        });
    }

    // Reveal chart and hide loader
    hideLoadingAndShowContent(loadingId, chartContainerId);
}
